import { listQuoteRequests } from '@/api/quote-requests'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { hasPermission } from '@/hooks/usePermission'
import { cn } from '@/lib/cn'
import { getLucideIcon } from '@/lib/lucideIcon'
import { MODULE_LABEL_KEYS } from '@/lib/moduleLabels'
import { useAuthStore } from '@/stores/auth.store'
import { useUIStore } from '@/stores/ui.store'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Globe,
  type LucideIcon,
  Package,
  X,
} from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface NavItem {
  label: string
  to: string
  icon: LucideIcon
  badge?: number
  group?: string
}

function bestMatchTo(pathname: string, items: NavItem[]): string | null {
  let best: string | null = null
  for (const it of items) {
    if (pathname === it.to || pathname.startsWith(`${it.to}/`)) {
      if (best === null || it.to.length > best.length) {
        best = it.to
      }
    }
  }
  return best
}

interface NavItemLinkProps {
  item: NavItem
  active: boolean
  collapsed: boolean
  onNavigate?: () => void
}

function NavItemLink({ item, active, collapsed, onNavigate }: NavItemLinkProps) {
  const Icon = item.icon
  const hasBadge = typeof item.badge === 'number' && item.badge > 0
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={cn(
        'group relative flex h-10 items-center gap-3 rounded-lg px-3 text-sm transition-colors',
        active
          ? 'bg-sidebar-active font-medium text-sidebar-active-foreground shadow-sm'
          : 'text-sidebar-foreground/75 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
        collapsed && 'h-10 w-10 justify-center p-0',
      )}
      title={collapsed ? item.label : undefined}
    >
      <span className="relative shrink-0">
        <Icon className="h-[18px] w-[18px]" />
        {hasBadge && collapsed ? (
          <span className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 rounded-full bg-accent shadow ring-2 ring-sidebar-background" />
        ) : null}
      </span>
      {!collapsed ? <span className="truncate">{item.label}</span> : null}
      {!collapsed && hasBadge ? (
        <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent px-1.5 text-[11px] font-bold tabular-nums text-accent-foreground">
          {(item.badge as number) > 99 ? '99+' : item.badge}
        </span>
      ) : null}
    </Link>
  )
}

function WorkspaceHeader({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation('workspaces')
  const { current } = useWorkspace()
  if (!current) return null
  const Icon = getLucideIcon(current.icon)
  const name = t(`workspaces.${current.code}.name`, current.name)

  if (collapsed) {
    return (
      <div className="mx-auto my-3 flex h-9 w-9 items-center justify-center rounded-xl bg-sidebar-accent text-sidebar-accent-foreground">
        <Icon className="h-[18px] w-[18px]" />
      </div>
    )
  }

  return (
    <div className="border-b border-sidebar-border px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-foreground/60">
        {t('switcher.label')}
      </p>
      <div className="mt-1 flex items-center gap-2">
        <Icon className="h-4 w-4 text-sidebar-foreground/70" />
        <span className="truncate text-sm font-semibold text-sidebar-foreground">{name}</span>
      </div>
    </div>
  )
}

interface OtherWorkspacesProps {
  collapsed: boolean
  onNavigate?: () => void
}

function OtherWorkspaces({ collapsed, onNavigate }: OtherWorkspacesProps) {
  const { t } = useTranslation('workspaces')
  const { current, available, switchTo } = useWorkspace()
  const others = useMemo(
    () =>
      [...available]
        .filter((w) => w.code !== current?.code)
        .sort((a, b) => b.default_priority - a.default_priority)
        .slice(0, 3),
    [available, current],
  )

  if (others.length === 0) return null

  return (
    <div className={cn('border-t border-sidebar-border', collapsed ? 'px-1 py-2' : 'px-2 py-3')}>
      {!collapsed ? (
        <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-foreground/60">
          {t('switcher.others')}
        </p>
      ) : null}
      <div className={cn('flex flex-col gap-0.5', collapsed && 'items-center')}>
        {others.map((ws) => {
          const Icon = getLucideIcon(ws.icon)
          const name = t(`workspaces.${ws.code}.name`, ws.name)
          return (
            <button
              key={ws.code}
              type="button"
              onClick={() => {
                switchTo(ws.code)
                onNavigate?.()
              }}
              className={cn(
                'flex items-center gap-2 rounded-lg text-left text-xs transition-colors',
                'text-sidebar-foreground/70 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
                collapsed ? 'h-9 w-9 justify-center' : 'w-full px-3 py-1.5',
              )}
              title={collapsed ? name : undefined}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {!collapsed ? <span className="truncate">{name}</span> : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface NavListProps {
  items: NavItem[]
  collapsed: boolean
  onNavigate?: () => void
}

function NavList({ items, collapsed, onNavigate }: NavListProps) {
  const location = useLocation()
  const { t } = useTranslation('common')
  const activeTo = useMemo(() => bestMatchTo(location.pathname, items), [location.pathname, items])
  const sections = useMemo(() => groupNav(items), [items])
  const grouped = sections.some((section) => section.group)

  return (
    <nav
      className={cn(
        'flex flex-1 flex-col gap-0.5 overflow-y-auto py-3',
        collapsed ? 'items-center px-1' : 'px-2',
      )}
    >
      {!collapsed && !grouped ? (
        <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-foreground/50">
          {t('nav.section_menu', 'Menu')}
        </p>
      ) : null}
      {sections.map((section) => (
        <div key={section.group || 'menu'} className="flex w-full flex-col gap-0.5">
          {!collapsed && section.group ? (
            <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-foreground/50 first:pt-0">
              {t(`nav.freight_group_${section.group}`, section.group)}
            </p>
          ) : null}
          {section.items.map((item) => (
            <NavItemLink
              key={item.to}
              item={item}
              active={item.to === activeTo}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ))}
    </nav>
  )
}

function groupNav(items: NavItem[]): { group: string; items: NavItem[] }[] {
  const order: string[] = []
  const buckets = new Map<string, NavItem[]>()
  for (const item of items) {
    const key = item.group ?? ''
    const bucket = buckets.get(key)
    if (bucket) {
      bucket.push(item)
    } else {
      order.push(key)
      buckets.set(key, [item])
    }
  }
  return order.map((group) => ({ group, items: buckets.get(group) ?? [] }))
}

function SidebarBrand({ collapsed }: { collapsed: boolean }) {
  return (
    <Link to="/dashboard" className="flex items-center gap-2.5">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sidebar-accent text-sidebar-accent-foreground shadow-sm">
        <Package className="h-[18px] w-[18px]" />
      </div>
      {!collapsed ? (
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold tracking-tight text-sidebar-foreground">
            AMANET
          </span>
          <span className="text-[10px] uppercase tracking-widest text-sidebar-foreground/60">
            Core
          </span>
        </div>
      ) : null}
    </Link>
  )
}

function useWorkspaceNavItems(): NavItem[] {
  const { current } = useWorkspace()
  const perms = useAuthStore((s) => s.permissions)
  const { t } = useTranslation('common')

  // Global unread-lead badge: only query when the quote-requests module is
  // actually in the current workspace (which already implies the read perm).
  // Same queryKey as CrmSubNav so both share one request/cache.
  const hasQuoteRequests = (current?.primary_modules ?? []).some(
    (m) =>
      m.path === '/crm/quote-requests' &&
      (!m.required_perm || hasPermission(perms, m.required_perm)),
  )
  const { data: qr } = useQuery({
    queryKey: ['crm', 'quote-requests', 'new-count'],
    queryFn: () => listQuoteRequests('new'),
    enabled: hasQuoteRequests,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  const newCount = qr?.quote_requests?.length ?? 0

  return useMemo<NavItem[]>(() => {
    if (!current) return []
    return current.primary_modules
      .filter((m) => !m.required_perm || hasPermission(perms, m.required_perm))
      .map((m) => {
        // Backend ships labels in French; the map below translates via the
        // `common:nav.*` namespace, falling back to the backend label when
        // the module is unmapped (e.g. a freshly added module).
        const i18nKey = MODULE_LABEL_KEYS[m.code]
        const label = i18nKey ? t(i18nKey, m.label) : m.label
        return {
          label,
          to: m.path,
          icon: getLucideIcon(m.icon),
          group: m.group,
          badge: m.path === '/crm/quote-requests' && newCount > 0 ? newCount : undefined,
        }
      })
  }, [current, perms, t, newCount])
}

function LearnLink({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation('learn')
  const location = useLocation()
  const active = location.pathname.startsWith('/learn')
  return (
    <div className={cn('border-t border-sidebar-border', collapsed ? 'px-1 py-2' : 'px-2 py-2')}>
      <Link
        to="/learn"
        onClick={onNavigate}
        className={cn(
          'group flex h-10 items-center gap-3 rounded-lg px-3 text-sm transition-colors',
          active
            ? 'bg-sidebar-active font-medium text-sidebar-active-foreground shadow-sm'
            : 'text-sidebar-foreground/75 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
          collapsed && 'h-10 w-10 justify-center p-0',
        )}
        title={collapsed ? t('nav.learn') : undefined}
      >
        <BookOpen className="h-[18px] w-[18px] shrink-0" />
        {!collapsed ? <span className="truncate">{t('nav.learn')}</span> : null}
      </Link>
    </div>
  )
}

function CmsLink({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const location = useLocation()
  const active = location.pathname.startsWith('/cms')
  return (
    <div className={cn(collapsed ? 'px-1 py-1' : 'px-2 py-1')}>
      <Link
        to="/cms"
        onClick={onNavigate}
        className={cn(
          'group flex h-10 items-center gap-3 rounded-lg px-3 text-sm transition-colors',
          active
            ? 'bg-sidebar-active font-medium text-sidebar-active-foreground shadow-sm'
            : 'text-sidebar-foreground/75 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
          collapsed && 'h-10 w-10 justify-center p-0',
        )}
        title={collapsed ? 'CMS Site Web' : undefined}
      >
        <Globe className="h-[18px] w-[18px] shrink-0" />
        {!collapsed ? <span className="truncate">CMS Site Web</span> : null}
      </Link>
    </div>
  )
}

export function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggle = useUIStore((s) => s.toggleSidebar)
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen)
  const closeMobile = useUIStore((s) => s.closeMobileSidebar)
  const { current } = useWorkspace()
  const items = useWorkspaceNavItems()

  useEffect(() => {
    if (!mobileOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [mobileOpen])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobile()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileOpen, closeMobile])

  return (
    <>
      {/* Desktop */}
      <aside
        className={cn(
          'sticky top-0 z-30 hidden h-dvh shrink-0 flex-col border-r border-sidebar-border bg-sidebar-background/95 backdrop-blur-sm transition-[width] duration-200 md:flex',
          collapsed ? 'w-[68px]' : 'w-64',
        )}
      >
        <div
          className={cn(
            'flex h-16 items-center border-b border-sidebar-border',
            collapsed ? 'justify-center px-2' : 'px-4',
          )}
        >
          <SidebarBrand collapsed={collapsed} />
        </div>
        {current ? <WorkspaceHeader collapsed={collapsed} /> : null}
        <NavList items={items} collapsed={collapsed} />
        <OtherWorkspaces collapsed={collapsed} />
        <LearnLink collapsed={collapsed} />
        <CmsLink collapsed={collapsed} />
        <button
          type="button"
          onClick={toggle}
          className="flex h-11 w-full items-center justify-center gap-2 border-t border-sidebar-border text-xs text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/40 hover:text-sidebar-foreground"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen ? (
          // biome-ignore lint/a11y/useSemanticElements: mobile drawer — using <dialog> would override the framer-motion overlay + aside siblings
          <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0 bg-background/50 backdrop-blur-sm"
              onClick={closeMobile}
              aria-hidden="true"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'tween', duration: 0.2 }}
              className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r border-sidebar-border bg-sidebar-background shadow-xl"
            >
              <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
                <SidebarBrand collapsed={false} />
                <button
                  type="button"
                  onClick={closeMobile}
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-sidebar-foreground/70 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {current ? <WorkspaceHeader collapsed={false} /> : null}
              <NavList items={items} collapsed={false} onNavigate={closeMobile} />
              <OtherWorkspaces collapsed={false} onNavigate={closeMobile} />
              <LearnLink collapsed={false} onNavigate={closeMobile} />
              <CmsLink collapsed={false} onNavigate={closeMobile} />
            </motion.aside>
          </div>
        ) : null}
      </AnimatePresence>
    </>
  )
}
